import { useState } from "react";

import { CalendarIcon } from "@radix-ui/react-icons";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Label } from "../../components/reusableComponents/Label";
import { Input } from "../../components/reusableComponents/Input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/reusableComponents/Popover";
import { Button } from "../../components/reusableComponents/Button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from "../../components/reusableComponents/Dialog";
import { Calendar } from "../../components/reusableComponents/Calendar";

export function PartialPaymentModal({ open, onOpenChange, facture, onSubmit }) {
  const [formData, setFormData] = useState({
    montant: "",
    datePaiement: new Date(),
    commentaire: "",
  });

  // Calcul du montant restant
  const montantRestant = facture.montantRestant || facture.montant;
  const nouveauMontantRestant =
    parseFloat(formData.montant) > 0
      ? montantRestant - parseFloat(formData.montant)
      : montantRestant;

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    const montantNumber = parseFloat(formData.montant || "0");
    if (montantNumber <= 0 || montantNumber > montantRestant) return;

    onSubmit(facture.id, {
      montant: montantNumber,
      datePaiement: format(formData.datePaiement, "yyyy-MM-dd"),
      commentaire: formData.commentaire,
    });

    onOpenChange(false);
    setFormData({ montant: "", datePaiement: new Date(), commentaire: "" });
  };

  const formatMontant = (montant) =>
    new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
    }).format(montant);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 overflow-hidden bg-white rounded-lg shadow-lg">
        {/* En-tête */}
        <div className="bg-gradient-to-r from-blue-700 to-blue-800 py-6 px-8">
          <div className="flex flex-col">
            <DialogTitle className="text-2xl font-bold tracking-tight text-white">
              Paiement partiel
            </DialogTitle>
            <p className="text-blue-100 mt-1">
              Enregistrer un paiement partiel pour la facture {facture.numero}
            </p>
          </div>
        </div>

        <div className="p-8 space-y-6">
          {/* Détails facture */}
          <div className="bg-blue-50 p-4 rounded-md border border-blue-100">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">Client</p>
                <p className="font-medium text-gray-800">{facture.client}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">N° Facture</p>
                <p className="font-medium text-gray-800">{facture.numero}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">
                  Montant total
                </p>
                <p className="font-medium text-gray-800">
                  {formatMontant(facture.montant)}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">
                  Montant restant
                </p>
                <p className="font-medium text-gray-800">
                  {formatMontant(montantRestant)}
                </p>
              </div>
            </div>
          </div>

          {/* Formulaire de paiement */}
          <div className="space-y-4">
            <div>
              <Label
                htmlFor="montant"
                className="text-sm font-medium text-gray-700"
              >
                Montant du paiement <span className="text-red-500">*</span>
              </Label>
              <div className="relative mt-1 rounded-md shadow-sm">
                <Input
                  id="montant"
                  type="number"
                  step="0.01"
                  value={formData.montant}
                  onChange={(e) => handleInputChange("montant", e.target.value)}
                  placeholder="0.00"
                  className="pr-12 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">€</span>
                </div>
              </div>
              {parseFloat(formData.montant) > montantRestant && (
                <p className="text-sm text-red-600 mt-1">
                  Le montant ne peut pas être supérieur au montant restant.
                </p>
              )}
              {nouveauMontantRestant > 0 &&
                parseFloat(formData.montant) > 0 &&
                parseFloat(formData.montant) <= montantRestant && (
                  <p className="text-sm text-blue-600 mt-1">
                    Après ce paiement, il restera{" "}
                    {formatMontant(nouveauMontantRestant)} à payer.
                  </p>
                )}
            </div>

            <div>
              <Label
                htmlFor="datePaiement"
                className="text-sm font-medium text-gray-700"
              >
                Date du paiement <span className="text-red-500">*</span>
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    id="datePaiement"
                    variant="outline"
                    className="w-full justify-start text-left font-normal mt-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.datePaiement ? (
                      format(formData.datePaiement, "dd MMMM yyyy", {
                        locale: fr,
                      })
                    ) : (
                      <span>Choisir une date</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.datePaiement}
                    onSelect={(date) => handleInputChange("datePaiement", date)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <Label
                htmlFor="commentaire"
                className="text-sm font-medium text-gray-700"
              >
                Commentaire
              </Label>
              <textarea
                id="commentaire"
                rows={2}
                className="w-full rounded-md border border-gray-300 focus:border-blue-500 focus:ring-blue-500 shadow-sm mt-1"
                value={formData.commentaire}
                onChange={(e) =>
                  handleInputChange("commentaire", e.target.value)
                }
                placeholder="Ex: Paiement par chèque..."
              />
            </div>
          </div>
        </div>

        {/* Pied du modal */}
        <DialogFooter className="flex justify-end px-8 py-6 border-t border-gray-200 bg-gray-50">
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-gray-300 hover:bg-gray-100 hover:text-gray-800"
            >
              Annuler
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              className="bg-blue-700 hover:bg-blue-800 text-white"
              disabled={
                !formData.montant ||
                parseFloat(formData.montant) <= 0 ||
                parseFloat(formData.montant) > montantRestant
              }
            >
              Enregistrer le paiement
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
